import { type GetServerSidePropsContext } from "next"
import z from "zod"
import { EnergyType } from "@/features/subscription"
import { OnboardingRouteNames, onboardingRoutesPaths } from "@/routes/onboarding"
import { ibanSchema } from "@/schemas/ibanSchema"
import { onboardingWizard } from "../utils/createOnboardingNextWizard"

const PostMeterDetailsSchema = z.union([
    z.object({
        selectedEnergyTypes: z.tuple([z.literal(EnergyType.Electricity)]),
        electricity: z.object({
            supplyPoint: z.object({
                id: z.string().nonempty(),
            }),
        }),
    }),
    z.object({
        selectedEnergyTypes: z.tuple([z.literal(EnergyType.Gas)]),
        gas: z.object({
            supplyPoint: z.object({
                id: z.string().nonempty(),
            }),
        }),
    }),
    z.object({
        selectedEnergyTypes: z.tuple([
            z.literal(EnergyType.Electricity),
            z.literal(EnergyType.Gas),
        ]),
        electricity: z.object({
            supplyPoint: z.object({
                id: z.string().nonempty(),
            }),
        }),
        gas: z.object({
            supplyPoint: z.object({
                id: z.string().nonempty(),
            }),
        }),
    }),
])

const PostQuoteSchema = z.intersection(
    PostMeterDetailsSchema,
    z.object({
        quoteCode: z.string().nonempty(),
    })
)

const PostAccountCreationSchema = z.intersection(
    PostQuoteSchema,
    z.object({
        accountNumber: z.string().nonempty(),
    })
)

const PostIbanSchema = z.intersection(
    PostAccountCreationSchema,
    z.object({
        iban: ibanSchema.nonempty(),
    })
)

const PostEnrollmentSchema = z.intersection(
    PostIbanSchema,
    z.union([
        z.object({
            selectedEnergyTypes: z.tuple([z.literal(EnergyType.Electricity)]),
            electricity: z.object({
                ledgerNumber: z.string().nonempty(),
            }),
        }),
        z.object({
            selectedEnergyTypes: z.tuple([z.literal(EnergyType.Gas)]),
            gas: z.object({
                ledgerNumber: z.string().nonempty(),
            }),
        }),
        z.object({
            selectedEnergyTypes: z.tuple([
                z.literal(EnergyType.Electricity),
                z.literal(EnergyType.Gas),
            ]),
            electricity: z.object({
                ledgerNumber: z.string().nonempty(),
            }),
            gas: z.object({
                ledgerNumber: z.string().nonempty(),
            }),
        }),
    ])
)

const SHEMAS_BY_STEPS: {
    [key in OnboardingRouteNames]?: z.ZodObject | z.ZodIntersection | z.ZodUnion
} = {
    // Post meterPoint details
    [OnboardingRouteNames.ProviderCalendar]: PostMeterDetailsSchema,
    [OnboardingRouteNames.EnergyLandscape]: PostMeterDetailsSchema,
    // Post quote
    [OnboardingRouteNames.Quote]: PostQuoteSchema,
    [OnboardingRouteNames.MeterReadings]: PostQuoteSchema,
    [OnboardingRouteNames.PersonalDetails]: PostQuoteSchema,
    // Post account creation
    [OnboardingRouteNames.Confirmation]: PostAccountCreationSchema,
    [OnboardingRouteNames.PaymentDetails]: PostAccountCreationSchema,
    [OnboardingRouteNames.PrePaymentCdf]: PostAccountCreationSchema,
    // Post IBAN
    [OnboardingRouteNames.PaymentDetailsConsent]: PostIbanSchema,
    [OnboardingRouteNames.OrderReview]: PostIbanSchema,
    [OnboardingRouteNames.Onboarded]: PostIbanSchema,
    [OnboardingRouteNames.PaymentDetailsRetry]: PostIbanSchema,
    [OnboardingRouteNames.PrePayment]: PostEnrollmentSchema,
    [OnboardingRouteNames.PaymentDetailsPendingPrepayment]: PostEnrollmentSchema,
}

export async function getOnboardingRouteGuardRedirect(
    stepName: OnboardingRouteNames,
    context: GetServerSidePropsContext
) {
    const onboardingState = await onboardingWizard.getCookieStateOnServer(context)

    const schema = SHEMAS_BY_STEPS[stepName]

    if (!schema || schema.safeParse(onboardingState.formValues).success) return

    if (!onboardingState.stepHistory.length) {
        return {
            redirect: {
                destination: onboardingRoutesPaths.meterDetails.resolve(),
                permanent: false,
            },
        }
    }

    let previousStepIndex = onboardingState.stepHistory.findIndex(
        (submittedStep) => submittedStep === stepName
    )

    if (previousStepIndex === -1) {
        previousStepIndex = onboardingState.stepHistory.length - 1
    }

    for (let index = previousStepIndex; index >= 0; index--) {
        const previousStepName = onboardingState.stepHistory[index]
        const previousSchema = SHEMAS_BY_STEPS[previousStepName]

        if (previousSchema && !previousSchema.safeParse(onboardingState.formValues).success) {
            continue
        }

        if (previousStepName === OnboardingRouteNames.MeterDetailsWithAffiliate) {
            return {
                redirect: {
                    destination: onboardingRoutesPaths.meterDetailsWithAffiliate.resolve({
                        queryParams: {
                            affiliateCode: onboardingState.formValues.affiliateCode ?? undefined,
                        },
                    }),
                    permanent: false,
                },
            }
        }

        if (previousStepName === OnboardingRouteNames.MeterDetailsWithPartner) {
            return {
                redirect: {
                    destination: onboardingState.formValues.partnerSlug
                        ? onboardingRoutesPaths.meterDetailsWithPartner.resolve({
                              pathParams: {
                                  partnerSlug: onboardingState.formValues.partnerSlug,
                              },
                          })
                        : onboardingRoutesPaths.meterDetails.resolve(),
                    permanent: false,
                },
            }
        }

        if (previousStepName === OnboardingRouteNames.MeterDetailsMonoEnergy) {
            return {
                redirect: {
                    destination: onboardingRoutesPaths.meterDetailsMonoEnergy.resolve({
                        pathParams: {
                            energyType: onboardingState.formValues.selectedEnergyTypes[0],
                        },
                        queryParams: {
                            propertyId: onboardingState.formValues.propertyId || undefined,
                        },
                    }),
                    permanent: false,
                },
            }
        }

        if (previousStepName === OnboardingRouteNames.MoveOut) {
            return {
                redirect: {
                    destination: onboardingRoutesPaths.moveOut.resolve({
                        queryParams: {
                            accountNumber: onboardingState.formValues.accountNumber,
                        },
                    }),
                    permanent: false,
                },
            }
        }

        return {
            redirect: {
                destination: onboardingRoutesPaths[previousStepName].resolve({}),
                permanent: false,
            },
        }
    }

    return {
        redirect: {
            destination: onboardingRoutesPaths.meterDetails.resolve(),
            permanent: false,
        },
    }
}
